/*
  HLL visual pass: helper for the restyled Judge.me review widget.

  The redesign of "Customer reviews" on the product page is done in CSS
  (section 12 of assets/hll-visual-pass.css). Judge.me still builds and
  owns every element, so nothing here adds, moves or rewrites its content.

  CSS cannot read a few values it needs, so this script copies them into
  CSS variables on the widget:
    --hll-score        the average rating, to fill the five stars
    --hll-on-product   " on Storkie", shown after "6 reviews"
    --hll-img-1..3     product photos for the "no reviews yet" state

  It also notes how many reviews the product has, as attributes on the
  widget, so the CSS can simplify small lists:
    data-hll-reviews="3"   the total
    data-hll-few           present when there are fewer than 5

  It also runs the photo carousel inside a review: the arrows, and which
  dot is active (--hll-i).

  If this script does not run, the widget still works. It loses the star
  fill, the product name, the empty state photos and the carousel arrows.
  The photos can still be swiped.
*/
(function () {
  'use strict';

  function cssString(value) {
    return '"' + String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"';
  }

  function setup(root) {
    var title = (root.getAttribute('data-product-title') || '').trim();
    if (title) root.style.setProperty('--hll-on-product', cssString(' on ' + title));

    function readScore() {
      var el = root.querySelector('.jm-average-rating-display p');
      var score = el ? parseFloat(el.textContent) : NaN;
      if (score >= 0 && score <= 5) root.style.setProperty('--hll-score', String(score));
    }

    var photosDone = false;
    function emptyStatePhotos() {
      if (photosDone || !root.querySelector('.jm-no-reviews-state')) return;
      photosDone = true;
      var path = window.location.pathname.replace(/\/$/, '');
      if (path.indexOf('/products/') === -1) return;
      window
        .fetch(path + '.js', { credentials: 'same-origin' })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (product) {
          var images = (product && product.images) || [];
          if (!images.length) return;
          images.slice(0, 3).forEach(function (src, index) {
            var url = (src.indexOf('//') === 0 ? 'https:' + src : src);
            // The whole photo, not a square crop, so heads and hems stay in
            url += (url.indexOf('?') === -1 ? '?' : '&') + 'width=360';
            root.style.setProperty('--hll-img-' + (index + 1), 'url(' + cssString(url) + ')');
          });
          // The tiles take the shape of the first product photo. The centre
          // tile is 120px wide, so its height is 120 divided by the photo's
          // width to height ratio. Kept between 120px and 200px in case a
          // product has an unusually wide or tall first photo.
          var ratio = 2 / 3;
          var media = (product && product.media) || [];
          for (var i = 0; i < media.length; i++) {
            if (media[i] && media[i].media_type === 'image' && media[i].aspect_ratio > 0) {
              ratio = media[i].aspect_ratio;
              break;
            }
          }
          var height = Math.max(120, Math.min(200, Math.round(120 / ratio)));
          root.style.setProperty('--hll-collage-h', height + 'px');
        })
        .catch(function () {});
    }

    // "19 reviews" -> 19. Kept at the highest value seen, so the total does
    // not drop if Judge.me ever shows a filtered count in the same place.
    var total = 0;
    function readCount() {
      var el = root.querySelector('.jm-average-rating-display p:last-child');
      var count = el ? parseInt(el.textContent.replace(/[^0-9]/g, ''), 10) : NaN;
      if (!(count > total)) return;
      total = count;
      root.setAttribute('data-hll-reviews', String(total));
      if (total < 5) root.setAttribute('data-hll-few', '');
      else root.removeAttribute('data-hll-few');
    }

    function refresh() {
      readScore();
      readCount();
      emptyStatePhotos();
    }

    /*
      Photo carousel inside a review. The CSS turns a review's photos into
      one tile that slides sideways. Two things need script:
        - the arrows: a tap near the left or right edge slides one photo
          instead of opening the popup
        - the dots: --hll-i tells the CSS which photo is showing
      Both listen on the widget, so nothing is attached to Judge.me's own
      elements and nothing is lost when it redraws the list.
    */
    function photoStrip(node) {
      var strip = node && node.closest ? node.closest('.jm-review-item > .jm-stack > .jm-cluster') : null;
      if (!strip || !strip.closest('.jdgm-review-list')) return null;
      return strip.querySelectorAll('.jm-media-thumbnail-list__thumbnail').length > 1 ? strip : null;
    }

    root.addEventListener(
      'scroll',
      function (event) {
        var strip = photoStrip(event.target);
        if (!strip || strip !== event.target || !strip.clientWidth) return;
        strip.style.setProperty('--hll-i', String(Math.round(strip.scrollLeft / strip.clientWidth)));
      },
      true
    );

    var EDGE = 34; // px from either side that counts as the arrow
    root.addEventListener(
      'click',
      function (event) {
        var strip = photoStrip(event.target);
        if (!strip) return;
        var box = strip.getBoundingClientRect();
        var x = event.clientX - box.left;
        if (x > EDGE && x < box.width - EDGE) return; // middle: open the popup
        event.preventDefault();
        event.stopPropagation();
        var calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        strip.scrollBy({ left: (x <= EDGE ? -1 : 1) * strip.clientWidth, behavior: calm ? 'auto' : 'smooth' });
      },
      true
    );

    refresh();

    // Judge.me fills the widget in after the page loads and redraws it on
    // sort, filter and page change. Re-read the values when that happens.
    if ('MutationObserver' in window) {
      var queued = false;
      new MutationObserver(function () {
        if (queued) return;
        queued = true;
        window.setTimeout(function () {
          queued = false;
          refresh();
        }, 200);
      }).observe(root, { childList: true, subtree: true });
    }
  }

  function boot() {
    if (!document.body.classList.contains('template-product')) return;
    var tries = 0;
    (function look() {
      var root = document.querySelector('.jdgm-review-widget.jdgm-widget-revamp');
      if (root) {
        setup(root);
        return;
      }
      if (++tries < 40) window.setTimeout(look, 250);
    })();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
